<template>
  <modal
    elem-id="modal_grace_grant"
    :title="t('Conceder carência')"
    :show="isVisible"
    size="small"
    :close-label="t('Fechar')"
    :save-label="t('Conceder')"
    :save-fn="handleSave"
    :save-disabled-fn="() => isSaving || !franquiaId || !days || !planId"
    save-test-id="grace-grant-submit"
    @close="closeModal"
    @dismiss="closeModal"
  >
    <template v-if="isVisible">
      <div class="grant-fields">
        <franchise-select
          v-model="franquiaId"
          elem-id="grace-grant-franchise"
          :label="t('Franquia')"
          data-testid="grace-grant-franchise-select"
        />
        <select-list
          v-model="planId"
          elem-id="grace-grant-plan"
          :label="t('Plano')"
          :itens-select="planOptions"
          :auto-select-first="false"
          :ng-required="true"
          data-testid="grace-grant-plan-select"
        />
        <input-number
          v-model="days"
          elem-id="grace-grant-days"
          :label="t('Dias')"
          data-testid="grace-grant-days-input"
        />
        <input-text
          v-model="reason"
          elem-id="grace-grant-reason"
          :label="t('Motivo') + ' (' + t('opcional') + ')'"
          data-testid="grace-grant-reason-input"
        />
      </div>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import { eventBus } from '../../../eventBus'
import { log } from '../../../utils/DebugLogger'
import { useGraceAdmin } from '../../../composables/useGraceAdmin'
import { useSubscriptionAdmin } from '../../../composables/useSubscriptionAdmin'
import Modal from '../../../ui/Modal.vue'
import InputNumber from '../../../inputs/InputNumber.vue'
import InputText from '../../../inputs/InputText.vue'
import SelectList from '../../../inputs/SelectList.vue'
import FranchiseSelect from '../../../inputs/FranchiseSelect.vue'

const { t } = useI18n()
const { grant } = useGraceAdmin()
const { groupedPlans, adminRows, listPlans, listAll } = useSubscriptionAdmin()

const isVisible = ref(false)
const isSaving = ref(false)
const franquiaId = ref<number | string | null>(null)
// '' (not null) is the empty sentinel: SelectList's modelValue is string|number
// and it emits '' when the selection is cleared.
const planId = ref<number | string>('')
const days = ref<number | null>(null)
const reason = ref<string>('')

// Only active plans: the backend rejects an inactive plan with `plan_inactive`.
const planOptions = computed(() =>
  groupedPlans.value
    .filter((p) => p.active === 1)
    .map((p) => ({ ID: p.id, Name: p.name })),
)

/**
 * Prefill the plan from the franchise's live subscription.
 *
 * Subscriptions_AdminSubscriptionRow carries plan_name but no plan_id, so the
 * match back to the catalog is by name and is only trustworthy when the name is
 * unique among active plans. On a duplicate (or no match) the field stays empty
 * and staff picks — a wrong plan_id would be written onto the grace row.
 */
function prefillPlan(): void {
  const fid = Number(franquiaId.value)
  if (!fid) return
  const liveRow = adminRows.value.find((r) => r.franquia_id === fid && r.is_live === true)
  if (!liveRow?.plan_name) return
  const matches = planOptions.value.filter((o) => o.Name === liveRow.plan_name)
  if (matches.length !== 1) {
    log('GraceGrantModal', 'prefillPlan', 'Plano não preenchido — nome ambíguo ou ausente no catálogo', {
      franquia_id: fid,
      plan_name: liveRow.plan_name,
      matches: matches.length,
    })
    return
  }
  planId.value = matches[0].ID
}

function resetFields(): void {
  franquiaId.value = null
  planId.value = ''
  days.value = null
  reason.value = ''
}

async function openModal(): Promise<void> {
  log('GraceGrantModal', 'openModal', 'Abrindo modal de concessão')
  resetFields()
  isVisible.value = true
  try {
    await Promise.all([listPlans(), listAll()])
  } catch (err) {
    log('GraceGrantModal', 'openModal', 'Erro ao carregar planos/assinaturas', err)
  }
  // FranchiseSelect may have auto-selected before the lists resolved — the
  // watcher below covers the opposite order.
  prefillPlan()
}

function closeModal(): void {
  log('GraceGrantModal', 'closeModal', 'Fechando modal de concessão')
  isVisible.value = false
  resetFields()
}

// The plan belongs to the franchise's subscription — changing franchise drops
// any previous choice and re-prefills.
watch(franquiaId, () => {
  planId.value = ''
  prefillPlan()
})

/**
 * Turn the backend's refusal into something staff can act on.
 *
 * useGraceAdmin already rejects with the backend's own code (the envelope
 * refuses on HTTP 200 and `throwOnEnvelopeFailure` turns that into a throw);
 * this catch then replaced it with "Erro ao conceder carência" — so a 10.000-day
 * grant on the Matriz produced a generic error while the real reason,
 * `grace_exceeds_cycle`, never reached the screen. Same message-dropping class as
 * the setup-fee 422 (2026-09-09).
 *
 * Unknown codes fall through VERBATIM: a code is a searchable fact, a generic
 * sentence is not.
 */
function humanizeGrantError(raw: string | undefined): string {
  const code = (raw ?? '').trim()
  const known: Record<string, string> = {
    grace_exceeds_cycle: t('A carência tem de ser menor que um ciclo de cobrança (máximo 27 dias). Para uma franquia que nunca é cobrada, o instrumento é a isenção, não a carência.'),
    live_subscription_exists: t('Esta franquia já tem uma assinatura viva — use "Estender carência" nela'),
    subscription_not_found: t('Assinatura não encontrada'),
    franquia_not_found: t('Franquia não encontrada'),
    plan_not_found: t('Plano não encontrado'),
    plan_inactive: t('O plano escolhido está inativo'),
    grace_grant_failed: t('Erro ao conceder carência'),
  }
  if (code === '') return t('Erro ao conceder carência')
  return known[code] ?? `${t('Erro ao conceder carência')}: ${code}`
}

async function handleSave(): Promise<void> {
  if (!franquiaId.value || !days.value || !planId.value) return
  isSaving.value = true
  try {
    await grant({
      franquia_id: Number(franquiaId.value),
      days: days.value,
      plan_id: Number(planId.value),
      ...(reason.value ? { reason: reason.value } : {}),
    })
    log('GraceGrantModal', 'handleSave', 'Carência concedida')
    eventBus.emit('globalMessage.success', t('Carência concedida com sucesso'))
    eventBus.emit('graceGrant.saved')
    closeModal()
    eventBus.emit('grace.refresh')
  } catch (err) {
    log('GraceGrantModal', 'handleSave', 'Erro ao conceder carência', err)
    eventBus.emit('globalMessage.error', humanizeGrantError((err as Error)?.message))
  } finally {
    isSaving.value = false
  }
}

onMounted(() => {
  eventBus.on('modal.graceGrant.open', openModal)
})

onBeforeUnmount(() => {
  eventBus.off('modal.graceGrant.open', openModal)
})
</script>

<style scoped>
.grant-fields {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
</style>
